// Module-level cache for page data that survives unmounts.
//
// Switching tabs unmounts a page, so every hook's useEffect re-ran and
// refetched from Supabase on the way back. Nothing was remembered between
// visits, which is why flicking between tabs never got faster.
//
// The cache lives at module scope rather than in a context, so it outlives
// any component tree and needs no provider wiring. It is memory only: a full
// page reload starts empty, which is the right trade for score data.
//
// TTL is chosen by the caller, because how long league data stays true
// depends entirely on whether football is being played right now. See
// ttlForGames below.
import { isGameLive } from "./gameStatus";

const cache = new Map();

// Scores only move while a game is in progress, and backend/espn.js only
// ingests them every 2 minutes, so refetching faster than that on the client
// cannot surface anything new.
export const LIVE_TTL_MS = 2 * 60 * 1000;

// Nothing is live: standings, lineups and final scores cannot change until
// the next kickoff. Held longer, but not indefinitely, so a manager's own
// edits elsewhere in the app still surface within a few minutes.
export const IDLE_TTL_MS = 5 * 60 * 1000;

// Rows that cannot change for the rest of the season: the teams table itself,
// and a past week's final standings. Held for a day rather than forever so a
// mid-season FBS membership change, or a correction to a finished week, still
// reaches a device that never signs out.
export const STATIC_TTL_MS = 24 * 60 * 60 * 1000;

/**
 * Picks a TTL from a week's games. Accepts any array of raw `games` rows
 * (game_status, period), which is what both the games table and the schedule
 * map's cached rawGames hold.
 *
 * A mid-game weather delay reports the same game_status ('delayed') as a
 * delay before kickoff, so a plain equality check against 'in_progress' used
 * to fall back to the idle TTL the moment a live game got delayed, going
 * stale for 5 minutes instead of refreshing every 2 right when a manager most
 * wants a current score. isGameLive tells the two delays apart via period.
 */
export function ttlForGames(games) {
  const anyLive = (games || []).some((g) => isGameLive({ gameStatus: g?.game_status, period: g?.period }));
  return anyLive ? LIVE_TTL_MS : IDLE_TTL_MS;
}

// Persistence is opt-in per key, and deliberately not the default. Scores go
// stale the moment a game moves, so a score cache that survived a relaunch
// would paint numbers that are wrong rather than merely old. Only data that
// is append-only, or true until a manager acts, is worth writing to disk.
//
// localStorage is the store: Capacitor gives the WebView a persistent origin,
// so it survives an app relaunch on iOS and Android as well as a browser
// reload. Every access is wrapped, since Safari in private mode throws on
// write rather than failing quietly.
const PERSIST_PREFIX = "lineup:cache:";

function readPersisted(key) {
  try {
    const raw = window.localStorage.getItem(PERSIST_PREFIX + key);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function writePersisted(key, entry) {
  try {
    window.localStorage.setItem(PERSIST_PREFIX + key, JSON.stringify(entry));
  } catch {
    // Quota or a blocked store. The memory cache still holds the value, so
    // this costs a refetch on the next launch and nothing else.
  }
}

/**
 * Drops every persisted entry. Call on sign-out: league data on disk outlives
 * the session that was allowed to read it, and the next person to use the
 * device may not be in that league.
 */
export function clearPersistedCache() {
  try {
    Object.keys(window.localStorage)
      .filter((k) => k.startsWith(PERSIST_PREFIX))
      .forEach((k) => window.localStorage.removeItem(k));
  } catch {
    // No store to clear.
  }
}

/**
 * Returns a value cached beyond its TTL, or null. Only persisted keys can
 * produce one, and only a caller that refetches straight away should ask:
 * this is the paint-something-immediately path, not a substitute for the
 * fetch.
 */
export function getStale(key) {
  const entry = cache.get(key) || readPersisted(key);
  return entry ? entry.value : null;
}

export function getCached(key) {
  // A persisted entry is promoted into memory on the first read after a
  // launch, so every later read in the session costs nothing.
  let entry = cache.get(key);
  if (!entry) {
    entry = readPersisted(key);
    if (entry) cache.set(key, entry);
  }
  if (!entry) return null;
  if (Date.now() > entry.expiresAt) {
    // Memory only. The disk copy is deliberately left behind for getStale,
    // which is called immediately after this returns null and is the whole
    // reason to persist: dropping it here would mean a cold launch always
    // painted an empty list. It is replaced by the fetch that follows, and
    // swept by invalidateCache and clearPersistedCache.
    cache.delete(key);
    return null;
  }
  return entry.value;
}

export function setCached(key, value, ttlMs, { persist = false } = {}) {
  const entry = { value, expiresAt: Date.now() + ttlMs };
  cache.set(key, entry);
  if (persist) writePersisted(key, entry);
}

/**
 * Drops cache entries whose key contains `fragment`. Call after a write, so
 * the next read reflects it rather than serving the pre-write copy for the
 * rest of the TTL.
 */
export function invalidateCache(fragment) {
  for (const key of cache.keys()) {
    if (key.includes(fragment)) cache.delete(key);
  }
  // Persisted keys are swept separately: a key written on a previous launch
  // is not in the memory map yet, so iterating that map alone would leave the
  // pre-write copy on disk to be read back after the next relaunch.
  try {
    Object.keys(window.localStorage).forEach((k) => {
      if (k.startsWith(PERSIST_PREFIX) && k.slice(PERSIST_PREFIX.length).includes(fragment)) {
        window.localStorage.removeItem(k);
      }
    });
  } catch {
    // No store to sweep.
  }
}

/**
 * Drops every cached entry belonging to one league.
 *
 * Call sites used to name each key prefix they knew about, which meant any
 * new cache added later was silently missed by every existing write. Every
 * key here is `<name>:<leagueId>:...`, so one league-wide sweep covers them
 * all. League data is small and refetches in one round trip, so dropping
 * slightly more than strictly necessary is the cheaper mistake.
 */
export function invalidateLeague(leagueId) {
  invalidateCache(`:${leagueId}`);
}

// Entries written while a game was live, so a resume during the live window
// can expire them early without touching anything else.
const liveKeys = new Set();

export function markLive(key) {
  liveKeys.add(key);
}

/**
 * Expires only the entries that were cached while football was being played.
 *
 * Coming back to the app is exactly when a stale score is most obvious, but
 * invalidating everything on every foreground would refetch the whole page
 * each time a manager glances at a text message, which is the load the cache
 * exists to avoid. Mid-week and off-season, resuming costs nothing.
 */
export function invalidateLiveOnResume() {
  for (const key of liveKeys) cache.delete(key);
  liveKeys.clear();
}

// Capacitor runs one long-lived WebView, so this fires on background/foreground
// in the app as well as on tab switches in a browser. No native plugin needed.
if (typeof document !== "undefined") {
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") invalidateLiveOnResume();
  });
}
